import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { AuthContext } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import Pagination from '../components/Pagination';
import { usePageSize } from '../hooks/usePagination';
import './Wishlists.css';
import './OwnerRegistrations.css';

const STATUSES = ['', 'pending', 'completed'];
const FOLLOW_UP_STATUSES = ['', 'new', 'contacted', 'interested', 'not_interested', 'closed'];

const STATUS_LABELS = {
  pending: 'Pending',
  completed: 'Completed'
};

const FOLLOW_UP_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  interested: 'Interested',
  not_interested: 'Not Interested',
  closed: 'Closed'
};

const TYPE_LABELS = {
  Hotel: 'Hotel',
  Spa: 'Spa',
  Tour: 'Tour',
  TravelPackage: 'Travel Package'
};

// Admin route segment and permission module for each listing type
const MODULE_BY_TYPE = {
  Hotel: 'hotels',
  Spa: 'spas',
  Tour: 'tours',
  TravelPackage: 'travel-packages'
};

// Admin detail page for each submitted listing type
const VIEW_PATH_BY_TYPE = {
  Hotel: '/sy-admin/hotels/view',
  Spa: '/sy-admin/spas/view',
  Tour: '/sy-admin/tours/view',
  TravelPackage: '/sy-admin/travel-packages/view'
};

const listingEditPath = (listing) =>
  listing?.listingId && MODULE_BY_TYPE[listing.listingType]
    ? `/sy-admin/${MODULE_BY_TYPE[listing.listingType]}/edit/${listing.listingId}`
    : null;

// "Add New ..." page, prefilled with the owner's draft
const draftCreatePath = (draft) =>
  draft?._id && MODULE_BY_TYPE[draft.listingType]
    ? `/sy-admin/${MODULE_BY_TYPE[draft.listingType]}/new?draft=${draft._id}`
    : null;

// Tab ids the public forms save as the owner's current step
const STEP_LABELS = {
  basic: 'Basic Info',
  location: 'Location',
  destination: 'Destination',
  rooms: 'Room Types',
  services: 'Services',
  options: 'Activity Options',
  variants: 'Package Variants',
  media: 'Photos',
  details: 'Details & Policies',
  policies: 'Policies',
  facilities: 'Facilities',
  contact: 'Contact',
  social: 'Social Links',
  additional: 'Additional',
  schedule: 'Schedule',
  duration: 'Duration & Departure',
  itinerary: 'Itinerary',
  inclusions: 'Inclusions / Exclusions',
  dates: 'Valid Dates',
  info: 'Important Info',
  status: 'Status'
};

// Friendly labels for the raw form keys; anything missing is humanised
const FIELD_LABELS = {
  name: 'Name',
  title: 'Title',
  agencyName: 'Agency name',
  category: 'Category',
  whatsappNumber: 'WhatsApp number',
  phone: 'Phone number',
  alternatePhone: 'Alternate mobile',
  googleListingLink: 'Google listing link',
  email: 'Email',
  website: 'Website',
  address: 'Address',
  displayAddress: 'Display address',
  city: 'City',
  state: 'State',
  country: 'Country',
  zipCode: 'Zip code',
  latitude: 'Latitude',
  longitude: 'Longitude',
  cityLatitude: 'City latitude',
  cityLongitude: 'City longitude',
  starRating: 'Star rating',
  shortDescription: 'Short description',
  description: 'Description',
  totalRooms: 'Total rooms',
  currency: 'Currency',
  checkInTime: 'Check-in time',
  checkOutTime: 'Check-out time',
  cancellationPolicy: 'Cancellation policy',
  paymentPolicy: 'Payment policy',
  petPolicy: 'Pet policy',
  smokingPolicy: 'Smoking policy',
  ageRestriction: 'Age restriction',
  facilities: 'Facilities',
  socialLinks: 'Social links',
  roomTypes: 'Room types',
  services: 'Services',
  amenities: 'Additional amenities',
  nearbyAttractions: 'Nearby attractions',
  languages: 'Languages spoken',
  pricePerNight: 'Final price / night',
  cutPrice: 'Price before discount',
  maxPersons: 'Max persons',
  bedType: 'Bed type',
  roomSize: 'Room size',
  roomAmenities: 'Room amenities',
  additionalAmenities: 'Additional amenities',
  serviceName: 'Service',
  price: 'Final price',
  duration: 'Duration',
  distance: 'Distance'
};

// Bookkeeping keys the browser saves that mean nothing to an admin
const HIDDEN_KEYS = new Set([
  'activeTab', 'addressInput', 'cityInput', 'keepDisplayAddressSame',
  'status', 'featured', 'verified', 'roomImages', 'serviceImages'
]);

const humanise = (key) =>
  String(key)
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

const labelFor = (key) => FIELD_LABELS[key] || humanise(key);

const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0 || value.every(isEmptyValue);
  if (typeof value === 'object') return Object.values(value).every(isEmptyValue);
  return false;
};

/** Renders any saved form value: text, numbers, lists, and nested objects. */
const DraftValue = ({ value, lookups }) => {
  if (isEmptyValue(value)) return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    if (lookups && lookups[value]) return lookups[value];
    if (/^https?:\/\//i.test(value)) {
      return <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>;
    }
    return <span style={{ whiteSpace: 'pre-wrap' }}>{value}</span>;
  }
  if (Array.isArray(value)) {
    const primitives = value.every((item) => item === null || typeof item !== 'object');
    if (primitives) {
      return value
        .filter((item) => !isEmptyValue(item))
        .map((item, index) => (
          <span key={index} className="owner-draft-pill">
            {lookups && lookups[item] ? lookups[item] : String(item)}
          </span>
        ));
    }
    return (
      <ul className="owner-draft-list">
        {value.map((item, index) => (
          <li key={index}>
            <DraftValue value={item} lookups={lookups} />
          </li>
        ))}
      </ul>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(([k, v]) => !HIDDEN_KEYS.has(k) && !isEmptyValue(v));
    if (entries.length === 0) return null;
    return (
      <div className="owner-inline-list">
        {entries.map(([k, v]) => (
          <span key={k}>
            <strong>{labelFor(k)}:</strong> <DraftValue value={v} lookups={lookups} />
          </span>
        ))}
      </div>
    );
  }
  return String(value);
};

/** One in-progress form: the step the owner reached and every field filled so far. */
const DraftCard = ({ draft, lookups, action }) => {
  const data = draft?.data || {};
  const form = data.formData && typeof data.formData === 'object' ? data.formData : {};
  const formEntries = Object.entries(form).filter(([k, v]) => !HIDDEN_KEYS.has(k) && !isEmptyValue(v));
  const extraEntries = Object.entries(data).filter(
    ([k, v]) => k !== 'formData' && !HIDDEN_KEYS.has(k) && !isEmptyValue(v)
  );
  const isEmpty = formEntries.length === 0 && extraEntries.length === 0;

  return (
    <div className="owner-draft-card">
      <div className="owner-draft-head">
        <h3>{TYPE_LABELS[draft.listingType] || draft.listingType} — in progress</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span className="owner-draft-meta">
            Reached step: <strong>{STEP_LABELS[draft.step] || draft.step || '—'}</strong>
            {' · '}Last saved {formatDateTime(draft.lastSavedAt)}
          </span>
          {action}
        </div>
      </div>
      {isEmpty ? (
        <div className="owner-draft-empty">The owner opened the form but has not filled anything in yet.</div>
      ) : (
        <table className="owner-draft-fields">
          <tbody>
            {formEntries.map(([key, value]) => (
              <tr key={key}>
                <td>{labelFor(key)}</td>
                <td><DraftValue value={value} lookups={lookups} /></td>
              </tr>
            ))}
            {extraEntries.map(([key, value]) => (
              <tr key={key}>
                <td>{labelFor(key)}</td>
                <td><DraftValue value={value} lookups={lookups} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const formatDateTime = (value) => {
  try {
    if (!value) return '';
    return new Date(value).toLocaleString();
  } catch {
    return String(value || '');
  }
};

const listingViewPath = (listing) =>
  listing?.listingId && VIEW_PATH_BY_TYPE[listing.listingType]
    ? `${VIEW_PATH_BY_TYPE[listing.listingType]}/${listing.listingId}`
    : null;

const getListingsLabel = (item) => {
  const listings = item?.submittedListings || [];
  if (listings.length === 0) return '—';
  return (
    <div className="owner-inline-list">
      {listings.map((l, index) => {
        const label = `${TYPE_LABELS[l.listingType] || l.listingType}: ${l.name || '(untitled)'}`;
        const path = listingViewPath(l);
        return path
          ? <Link key={`${l.listingId}-${index}`} to={path}>{label}</Link>
          : <span key={`${l.listingId}-${index}`}>{label}</span>;
      })}
    </div>
  );
};

const getDraftsLabel = (item) => {
  const drafts = item?.drafts || [];
  if (drafts.length === 0) return '—';
  return (
    <div className="owner-inline-list">
      {drafts.map((d) => (
        <span key={d._id || d.listingType}>
          {TYPE_LABELS[d.listingType] || d.listingType}: {d.name || '(no name yet)'}
          <span style={{ color: '#94a3b8', fontSize: 12 }}> · {STEP_LABELS[d.step] || d.step || 'started'}</span>
        </span>
      ))}
    </div>
  );
};

const OwnerRegistrations = () => {
  const navigate = useNavigate();
  const { hasPermission } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ pending: 0, completed: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ?status= (from the dashboard) picks the initial view; pending by default
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(() => (STATUSES.includes(searchParams.get('status')) ? searchParams.get('status') : 'pending'));
  const [followUpStatus, setFollowUpStatus] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [pageSize, setPageSize] = usePageSize();

  const [selected, setSelected] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [lookups, setLookups] = useState({});
  const [updating, setUpdating] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (followUpStatus) params.set('followUpStatus', followUpStatus);
    if (q) params.set('q', q);
    params.set('page', String(page));
    params.set('limit', String(pageSize));
    return params.toString();
  }, [status, followUpStatus, q, page, pageSize]);

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API_BASE_URL}/admin/owner-registrations?${queryParams}`);
      if (res.data?.success) {
        setItems(res.data.data || []);
        setTotal(res.data.total || 0);
        setPages(res.data.pages || 1);
        setStats(res.data.stats || { pending: 0, completed: 0, all: 0 });
      } else {
        setError(res.data?.message || 'Failed to load owner registrations');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load owner registrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  // Drafts store category / facility ids; show their names when we can read them
  useEffect(() => {
    const load = async () => {
      const map = {};
      const sources = [
        `${API_BASE_URL}/admin/hotel-categories`,
        `${API_BASE_URL}/admin/facilities`,
        `${API_BASE_URL}/admin/amenities`
      ];
      await Promise.all(
        sources.map(async (url) => {
          try {
            const res = await axios.get(url);
            (res.data?.data || []).forEach((doc) => {
              if (doc?._id && doc?.name) map[doc._id] = doc.name;
            });
          } catch (e) {
            // Optional: without permission the raw id is shown instead
          }
        })
      );
      setLookups(map);
    };
    load();
  }, []);

  const openDetails = async (item) => {
    setSelected(item);
    setSelectedFollowUp(item.followUpStatus || 'new');
    setAdminNotes(item.adminNotes || '');
    // The list only carries draft summaries; fetch the full filled-in data
    try {
      setDetailsLoading(true);
      const res = await axios.get(`${API_BASE_URL}/admin/owner-registrations/${item._id}`);
      if (res.data?.success && res.data.data) {
        setSelected((current) => (current && current._id === item._id ? { ...current, ...res.data.data } : current));
      }
    } catch (e) {
      // Keep the summary view if the detail call fails
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setSelected(null);
    setSelectedFollowUp('');
    setAdminNotes('');
  };

  const saveDetails = async () => {
    if (!selected?._id) return;
    try {
      setUpdating(true);
      const res = await axios.put(`${API_BASE_URL}/admin/owner-registrations/${selected._id}`, {
        followUpStatus: selectedFollowUp,
        adminNotes
      });
      if (res.data?.success) {
        closeDetails();
        await fetchRegistrations();
      } else {
        alert(res.data?.message || 'Failed to update registration');
      }
    } catch (e) {
      alert(e?.response?.data?.message || e.message || 'Failed to update registration');
    } finally {
      setUpdating(false);
    }
  };

  const deleteRegistration = async (item) => {
    if (!item?._id) return;
    if (!window.confirm(`Delete the registration lead for ${item.phone}? This cannot be undone.`)) {
      return;
    }
    try {
      setUpdating(true);
      const res = await axios.delete(`${API_BASE_URL}/admin/owner-registrations/${item._id}`);
      if (res.data?.success) {
        closeDetails();
        await fetchRegistrations();
      } else {
        alert(res.data?.message || 'Failed to delete registration');
      }
    } catch (e) {
      alert(e?.response?.data?.message || e.message || 'Failed to delete registration');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Layout>
      <div className="container">
        <div className="wishlists-header">
          <div>
            <h1>Owner Registrations</h1>
            <p style={{ color: '#64748b', marginTop: 6 }}>
              Property owners who verified their phone on &ldquo;List your property&rdquo; — including those who never finished the form.
            </p>
          </div>
          <div className="header-stats">
            <span className="stat-badge">Pending: {stats.pending}</span>
            <span className="stat-badge">Completed: {stats.completed}</span>
            <span className="stat-badge">Showing: {items.length}</span>
          </div>
        </div>

        <div className="customers-filters">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              className="filter-select"
              value={status}
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value);
              }}
              title="Status"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s ? STATUS_LABELS[s] : 'All Status'}
                </option>
              ))}
            </select>

            <select
              className="filter-select"
              value={followUpStatus}
              onChange={(e) => {
                setPage(1);
                setFollowUpStatus(e.target.value);
              }}
              title="Follow-up"
            >
              {FOLLOW_UP_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s ? FOLLOW_UP_LABELS[s] : 'All Follow-ups'}
                </option>
              ))}
            </select>
          </div>

          <div className="search-box">
            <input
              className="search-input"
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder="Search phone / listing / notes…"
            />
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}

        {(!loading && items.length === 0) ? (
          <div className="empty-state">
            <span className="empty-state-icon">📝</span>
            <h2>No owner registrations found</h2>
            <p>Try adjusting filters or search.</p>
          </div>
        ) : (
          <div className="wishlists-table-container">
            <table className="wishlists-table">
              <thead>
                <tr>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>OTP Verified</th>
                  <th>Attempts</th>
                  <th>Listings Submitted</th>
                  <th>In Progress</th>
                  <th>Follow-up</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8}>Loading…</td></tr>
                ) : (
                  items.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <strong>{item.phone}</strong>
                          <div style={{ display: 'flex', gap: 10, fontSize: 12 }}>
                            <a href={`tel:${item.phone}`}>Call</a>
                            <a
                              href={`https://wa.me/91${item.phone}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              WhatsApp
                            </a>
                          </div>
                        </div>
                      </td>
                      <td>{STATUS_LABELS[item.status] || item.status}</td>
                      <td>{formatDateTime(item.lastVerifiedAt)}</td>
                      <td>{item.verifyCount || 0}</td>
                      <td>{getListingsLabel(item)}</td>
                      <td>{getDraftsLabel(item)}</td>
                      <td>{FOLLOW_UP_LABELS[item.followUpStatus] || item.followUpStatus}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn btn-secondary" onClick={() => openDetails(item)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page}
          pages={pages}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          loading={loading}
          noun="registrations"
        />

        {selected && (
          <div className="customers-modal" onClick={closeDetails} role="presentation">
            <div className="customers-modal-content" onClick={(e) => e.stopPropagation()} role="presentation" style={{ maxWidth: 720 }}>
              <div className="customers-modal-header">
                <h2>Registration Details</h2>
                <button className="modal-close" onClick={closeDetails}>×</button>
              </div>

              <div className="customers-modal-body">
                <div className="customers-table-container" style={{ marginBottom: 16 }}>
                  <table className="customers-table">
                    <thead>
                      <tr>
                        <th style={{ width: 180 }}>Field</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Phone</strong></td>
                        <td>
                          {selected.phone}
                          {'  '}
                          <a href={`tel:${selected.phone}`} style={{ marginLeft: 10, fontSize: 12 }}>Call</a>
                          <a
                            href={`https://wa.me/91${selected.phone}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ marginLeft: 10, fontSize: 12 }}
                          >
                            WhatsApp
                          </a>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Status</strong></td>
                        <td>{STATUS_LABELS[selected.status] || selected.status}</td>
                      </tr>
                      <tr>
                        <td><strong>First verified</strong></td>
                        <td>{formatDateTime(selected.firstVerifiedAt)}</td>
                      </tr>
                      <tr>
                        <td><strong>Last verified</strong></td>
                        <td>{formatDateTime(selected.lastVerifiedAt)}</td>
                      </tr>
                      <tr>
                        <td><strong>Verify attempts</strong></td>
                        <td>{selected.verifyCount || 0}</td>
                      </tr>
                      <tr>
                        <td><strong>Completed at</strong></td>
                        <td>{selected.completedAt ? formatDateTime(selected.completedAt) : '—'}</td>
                      </tr>
                      <tr>
                        <td><strong>Listings submitted</strong></td>
                        <td>
                          {(selected.submittedListings || []).length === 0 ? '—' : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {selected.submittedListings.map((l, index) => {
                                const path = listingViewPath(l);
                                const text = `${TYPE_LABELS[l.listingType] || l.listingType}: ${l.name || '(untitled)'}${l.city ? ` — ${l.city}` : ''}`;
                                const editPath = listingEditPath(l);
                                const canEdit = editPath && hasPermission(`${MODULE_BY_TYPE[l.listingType]}.update`);
                                return (
                                  <span key={`${l.listingId}-${index}`}>
                                    {path ? <Link to={path}>{text}</Link> : text}
                                    {' '}({formatDateTime(l.submittedAt)})
                                    {canEdit && (
                                      <Link to={editPath} className="btn btn-secondary" style={{ marginLeft: 10, padding: '2px 10px', fontSize: 12 }}>
                                        Edit
                                      </Link>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="owner-drafts">
                  <div className="owner-draft-group" style={{ background: 'transparent', padding: '0 0 4px' }}>
                    Filled so far (not yet submitted)
                  </div>
                  {detailsLoading && !(selected.drafts || []).some((d) => d.data) ? (
                    <div className="owner-draft-empty">Loading filled details…</div>
                  ) : (selected.drafts || []).length === 0 ? (
                    <div className="owner-draft-empty">No form in progress for this phone.</div>
                  ) : (
                    selected.drafts.map((draft) => {
                      const createPath = draftCreatePath(draft);
                      const canCreate = createPath && hasPermission(`${MODULE_BY_TYPE[draft.listingType]}.create`);
                      return (
                        <DraftCard
                          key={draft._id || draft.listingType}
                          draft={draft}
                          lookups={lookups}
                          action={canCreate ? (
                            <button
                              type="button"
                              className="btn btn-primary"
                              style={{ padding: '6px 14px', fontSize: 13 }}
                              onClick={() => navigate(createPath)}
                              title="Open the Add page with these details prefilled"
                            >
                              Edit &amp; create listing
                            </button>
                          ) : null}
                        />
                      );
                    })
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: 12, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                      Follow-up status
                    </label>
                    <select
                      className="filter-select"
                      value={selectedFollowUp}
                      onChange={(e) => setSelectedFollowUp(e.target.value)}
                    >
                      {FOLLOW_UP_STATUSES.filter(Boolean).map((s) => (
                        <option key={s} value={s}>{FOLLOW_UP_LABELS[s]}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: 12, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                      Admin notes
                    </label>
                    <textarea
                      className="search-input"
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={4}
                      placeholder="Add notes for your team…"
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: 24, paddingTop: 0 }}>
                <button className="btn btn-danger" onClick={() => deleteRegistration(selected)} disabled={updating}>
                  Delete
                </button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" onClick={closeDetails} disabled={updating}>Cancel</button>
                  <button className="btn btn-primary" onClick={saveDetails} disabled={updating}>
                    {updating ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OwnerRegistrations;
